import { useQuery, useMutation, useQueryClient } from "react-query";
import { 
  fetchUserCtx, 
  resetPassword, 
  fetchUsersInformation, 
  registerUserInTenantApp,
  deactivateUserInTenantApp
} from "./services/SettingsService";

import { useStoreInformation } from "../viewModel/stores/storeInformationStore";
import { textFormatter } from "../utils/formatter/formatter";
import { CACHE_TTL_MS } from "./ttl.cache";

const otimisticUpdate = (newUser) => {
  return {
    username: newUser.data.username,
    email: newUser.data.email,
    role: newUser.data.role,
    department: textFormatter(newUser.data.role),
    stores: Array.isArray(newUser.data.stores) ? newUser.data.stores.join(", ") : newUser.data.stores,
    storeIds: [],
  };
}

export const useUserCtx = (options = {}) => {

  const queryClient = useQueryClient();

  const { 
    shouldFetchUserCtx = false,
    shouldFetchUsersInformation = false
   } = options;

  const query = useQuery({
    queryKey: ["userCtx"],

    queryFn: async () => {
      const data = await fetchUserCtx();
      return data;
    },

    staleTime: CACHE_TTL_MS.TTL_24_HOURS,
    cacheTime: CACHE_TTL_MS.TTL_24_HOURS,
    refetchOnMount: true,
    enabled: shouldFetchUserCtx,

    select: (data) => {
      const stores = data?.storesOnUsers || [];

      const allStores = stores.map((store) => {
        const storeNumber = (() => {
          const match = store.storeName.match(/\d+$/);
          return match ? parseInt(match[0]) : null;
        })();

        const regionName = store.storeName.replace(/\s*\d+$/, "").trim();

        return {
          storeId: store.storeId,
          storeName: store.storeName,
          storeNumber,
          regionName,
        };
      });

      const regionMap = {};

      allStores.forEach((store) => {
        if (!regionMap[store.regionName]) {
          regionMap[store.regionName] = [];
        }
        regionMap[store.regionName].push({
          storeId: store.storeId,
          storeNumber: store.storeNumber,
          storeName: store.storeName,
        });
      });

      const allRegions = Object.entries(regionMap).map(([regionName, stores]) => ({
        regionName,
        stores,
      }));

      return {
        userId: data?.userId,
        stores: data?.storesOnUsers || [],
        role: data?.role || null,
        allStores,
        allRegions
      };
    },

    onSuccess: (transformedData) => {
      if (transformedData?.allStores) {
        const storeNumbers = transformedData.allStores
          .map((s) => s.storeNumber)
          .filter((n) => n !== null);
        
        useStoreInformation.getState().setAvailableStores(storeNumbers);

        if (transformedData.allStores.length === 1) {
          const { storeId, storeNumber } = transformedData.allStores[0];
          useStoreInformation.getState().setStore(storeId, storeNumber);
        }
      }
    },

    ...options,
  });

  const usersInformationQuery = useQuery({
    queryKey: ["usersInformation"],

    queryFn: async () => {
      const data = await fetchUsersInformation();
      return data;
    },

    staleTime: 0,
    cacheTime: 0,
    refetchOnMount: true,
    enabled: shouldFetchUsersInformation,
  });

  const resetPasswordMutation = useMutation({
    mutationFn: async (password) => {
      return await resetPassword(password);
    },
  });

  const deactivateUserInTenantAppMutation = useMutation({
    mutationFn: async (userId) => {
      return await deactivateUserInTenantApp(userId);
    },

    onSuccess: async (response) => {
      const deactivatedUserId = response.user;

      queryClient.setQueryData(["usersInformation"], (oldData) => {
        if (!oldData) return oldData;

        return {
          ...oldData,
          totalUsers: oldData.totalUsers - 1,
          users: oldData.users.filter(
            (user) => user.userId !== deactivatedUserId
          ),
        };
      });
    },
  });

  const registerUserMutation = useMutation({
    mutationFn: async (payload) => {
      return await registerUserInTenantApp(payload);
    },

    onSuccess: async (newUser) => {
      const transformedUser = otimisticUpdate(newUser);

      queryClient.setQueryData(["usersInformation"], (oldData) => {

        return {
          ...oldData,
          users: [...oldData.users, transformedUser],
        };
      });

      await queryClient.invalidateQueries(["usersInformation"]);
    },
   
  });

  return {
    userCtx: query,
    isLoadingStores: query.isLoading,
    resetPassword: resetPasswordMutation.mutateAsync,
    deactivateUserInTenantApp: deactivateUserInTenantAppMutation.mutateAsync,
    isDeactivatingUser: deactivateUserInTenantAppMutation.isLoading,
    isResettingPassword: resetPasswordMutation.isLoading,
    resetPasswordError: resetPasswordMutation.error,
    usersInformation: usersInformationQuery.data,
    isLoadingUsersInformation: usersInformationQuery.isLoading,
    usersInformationError: usersInformationQuery.error,
    registerUser: registerUserMutation.mutateAsync,
    isRegisteringUser: registerUserMutation.isLoading,
    registerUserError: registerUserMutation.error,
    onSuccessRegister: registerUserMutation.isSuccess
  };
};